import {
  Brand,
  IconHome,
  IconQueue,
  IconSearch,
  IconUser,
  Rail,
  RailItem,
  RailQueue,
} from '@akpl/ui-tv';
import { useMemo } from 'react';

import { RAIL_ITEMS, type RailItemKey, type ScreenName, useRouter } from '../router';
import { queueByStatus, selectNowPlayingId, selectQueuedCount, useStore } from '../store';
import { useT } from '../useT';
import { PhoneQr } from './PhoneQr';

/**
 * How many queued songs the expanded rail spells out before it falls back to "+N más". Two,
 * since the phone QR took the foot of the rail (owner 2026-10-05): 1080 px holds the brand,
 * the four items, two named songs + "+N más" and the 180 px QR without anything overlapping.
 */
const RAIL_QUEUE_LIMIT = 2;

const ICONS = { home: IconHome, search: IconSearch, profile: IconUser, queue: IconQueue } as const;

/**
 * Post-auth rail (docs/04 §7.1): Home, Buscar, Perfil, Fila (badge = queued count). Icon-only
 * until it gains focus, then an overlay above the content. "Fila" opens the Player with the
 * queue drawer instead of a screen of its own (docs/04 §7.6).
 */
export function AppRail({
  activeScreen,
  onOpenQueue,
}: {
  activeScreen: ScreenName;
  /** Opens the queue drawer *over the current screen* (UX-071). */
  onOpenQueue: () => void;
}) {
  const { t } = useT();
  const reset = useRouter((s) => s.reset);
  const navigate = useRouter((s) => s.navigate);
  const queued = useStore(selectQueuedCount);
  const queue = useStore((s) => s.queue);
  const playingId = useStore(selectNowPlayingId);
  // UX-192: the collapsed rail only ever showed a count, so a room adding songs from its
  // phones could not tell what was waiting. Expanded, it now names them.
  const entries = useMemo(
    () =>
      queueByStatus(queue, 'queued')
        .slice(0, RAIL_QUEUE_LIMIT)
        .map((entry) => ({
          id: entry.id,
          title: entry.title,
          subtitle: entry.addedBy?.name
            ? t('tv:player.addedBy', { name: entry.addedBy.name })
            : entry.artistNames,
        })),
    [queue, t],
  );

  const select = (key: string) => {
    const item = key as RailItemKey;
    if (item === 'queue') {
      /**
       * UX-071: "Fila" used to swap the screen the room was looking at for a black idle player
       * with a stale "los controles se ocultan en 10 s" chip behind the empty drawer — Home,
       * hero and rails, gone for as long as the drawer was open. The drawer is a drawer: it
       * opens *over* whatever is on screen. Only when the player is already the screen (or
       * something is playing on it) does it go through the player, which owns its own copy.
       */
      if (activeScreen === 'player' || playingId) navigate('player', { drawer: true });
      else onOpenQueue();
      return;
    }
    if (item === 'home') {
      reset('home');
      return;
    }
    reset('home');
    navigate(item);
  };

  const isActive = (item: RailItemKey) =>
    item === 'queue' ? activeScreen === 'player' : activeScreen === item;

  return (
    <Rail
      brand={<Brand name={t('common:app.name')} />}
      // Owner 2026-10-05: the "El menú se abre sobre el contenido…" hint is gone; the foot of
      // the rail carries the phone QR instead, so a guest can send songs from there.
      footer={<PhoneQr compact data-testid="rail-qr" />}
    >
      {RAIL_ITEMS.map((item) => {
        const Icon = ICONS[item];
        return (
          <RailItem
            key={item}
            itemKey={item}
            label={t(`tv:rail.${item}`)}
            icon={<Icon size={40} />}
            active={isActive(item)}
            badge={item === 'queue' ? queued : undefined}
            onSelect={select}
          />
        );
      })}
      <RailQueue
        title={t('tv:rail.queueTitle')}
        entries={entries}
        empty={t('tv:rail.queueEmpty')}
        more={
          queued > entries.length
            ? t('tv:rail.moreInQueue', { count: queued - entries.length })
            : undefined
        }
      />
    </Rail>
  );
}
